import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';

import {
  CatalogItemFullDTO, ShoppingDTO, CartItemDTO, PriceDTO,
  ShoppingDTOStatusEnum, CartItemDTOStatusEnum,
  CommentDTO, AuthorDTO, CartItemRejectInfoDTO,
} from '@/api';
import services from '@/services';
import { throwError } from '@/utils';

interface IShoppingSave {
  id?: number,
  clientId: number,
  title: string;
  status: ShoppingDTOStatusEnum,
  budget?: number,
  shoppingDate?: number,
  startTime?: number,
  finishTime?: number,
  comments: string,
  studioId?: number,
}

export enum GroupTypeEnum {
  BY_CATEGORY = 'category',
  BY_SUPPLIER = 'supplierName',
}

export type RejectStatus = {
  type: 'style' | 'fit' | 'color' | 'fabric' | 'print' | 'other' | 'price';
  comment: string;
}

@Module({ name: 'ShoppingModule' })
export default class ShoppingModule extends VuexModule {
  loading = false;

  current: ShoppingDTO | null = null

  items: CartItemDTO[] = [];

  comments: CommentDTO[] = [];

  selected: number[] = [];

  multi = false;

  groupBy = GroupTypeEnum.BY_CATEGORY;

  displayList: 'thumbnails' | 'list' = 'thumbnails';

  displayScreen: 'things' | 'looks' = 'things';

  get selectedThings() {
    return this.items.filter(thing => thing.id && this.selected.includes(thing.id));
  }

  get statuses() {
    return this.items.reduce<string[]>((acc, thing) => {
      const { status } = thing;
      return status && !acc.includes(status) ? acc.concat(status) : acc;
    }, []);
  }

  get groupByStatus() {
    const map: {[k in CartItemDTOStatusEnum]: CartItemDTO[]} = {
      SAND_BOX: [],
      BUYING: [],
      FITTING: [],
      BOUGHT: [],
      REJECTED: [],
    };
    this.items.forEach((item) => {
      if (item.status) {
        map[item.status].push(item);
      }
    });
    return map;
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  @Mutation
  setSelected(ids: number[]) {
    this.selected = ids;
  }

  @Mutation
  setMulti(multi: boolean) {
    this.multi = multi;
  }

  @Mutation
  setCurrentShopping(shopping: ShoppingDTO) {
    const { items, ...other } = shopping;
    this.current = other;
    if (items) {
      this.items = items;
    }
  }

  @Mutation
  setQuantity({ cartItemIds, quantity }:
    { cartItemIds: number[]; quantity?: number }) {
    this.items.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      if (item.id && cartItemIds.includes(item.id)) item.quantity = quantity;
    });
  }

  @Mutation
  setRejectStatus({ cartItemIds, rejectStatus }:
    { cartItemIds: number[]; rejectStatus: CartItemRejectInfoDTO }) {
    this.items.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      if (item.id && cartItemIds.includes(item.id)) item.rejectStatus = [rejectStatus];
    });
  }

  @Mutation
  setAvailable({ cartItemIds, available }:
    { cartItemIds: number[]; available?: boolean }) {
    this.items.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      if (item.id && cartItemIds.includes(item.id)) item.available = available;
    });
  }

  @Mutation
  setGrouping(type: GroupTypeEnum) {
    this.groupBy = type;
  }

  @Mutation
  setDisplayList(value: 'thumbnails' | 'list') {
    this.displayList = value;
  }

  @Mutation
  setDisplayScreen(value: 'things' | 'looks') {
    this.displayScreen = value;
  }

  @Mutation
  addItem(item: CartItemDTO) {
    this.items.push(item);
  }

  @Mutation
  removeItem(shoppingItemIds: number[]) {
    this.items = this.items.filter(thing => thing.id && !shoppingItemIds.includes(thing.id));
  }

  @Mutation
  setComments(comments: CommentDTO[]) {
    this.comments = comments;
  }

  @Mutation
  addComments({ id, text, author }: {id: number; text: string; author: AuthorDTO }) {
    const comment: CommentDTO = {
      id,
      text,
      author,
      createdAt: new Date().valueOf(),
    };
    this.comments.unshift(comment);
  }

  @Action({ rawError: true, commit: 'setCurrentShopping' })
  async fetchShopping(shoppingId: number) {
    try {
      this.setLoading(true);
      const { data } = await services.shoppingApi().shoppingEndpointGetShopping(shoppingId);
      amplitude.getInstance().logEvent('Загрузка шоппинга');
      return data;
    } catch (e) {
      return throwError('Ошибка получения шоппинга')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async saveShopping(shopping: IShoppingSave) {
    await services.shoppingApi()
      .shoppingEndpointSaveShopping(
        shopping.clientId,
        shopping.title,
        shopping.id,
        shopping.comments,
        shopping.status,
        shopping.shoppingDate,
        shopping.startTime || undefined,
        shopping.finishTime || undefined,
        shopping.budget,
        shopping.studioId || undefined,
      );
    amplitude.getInstance().logEvent('Сохранение шоппинга', shopping);
  }

  @Action({ rawError: true })
  async saveShoppingItem({ catalogItem, catalogItemPrice, quantity }:
    { catalogItem: CatalogItemFullDTO; catalogItemPrice: PriceDTO; quantity?: number }) {
    const shoppingId = this.current && this.current.id;
    if (shoppingId != null && catalogItem.id != null && catalogItemPrice.id != null) {
      const { data: cartItem } = await services.shoppingApi()
        .shoppingEndpointAddCartItem(shoppingId, catalogItem.id, catalogItemPrice.id, quantity);
      this.addItem(cartItem);
      amplitude.getInstance().logEvent('Сохранение вещи в корзину', { catalogItem, catalogItemPrice, quantity });
    }
  }

  @Action({ rawError: true })
  async saveItemsQuantity({ cartItemIds, quantity }: { cartItemIds: number[]; quantity: number; }) {
    await services.shoppingApi()
      .shoppingEndpointEditCartItem(cartItemIds.map(id => ({ id, quantity })));
    this.setQuantity({ cartItemIds, quantity });
    amplitude.getInstance().logEvent('Сохранение количества вещи', { cartItemIds, quantity });
  }

  @Action({ rawError: true })
  async saveItemsRejectStatus({
    cartItemIds, rejectStatus,
  }: { cartItemIds: number[]; rejectStatus: CartItemRejectInfoDTO }) {
    await services.shoppingApi()
      .shoppingEndpointEditCartItem(cartItemIds.map(id => ({ id, rejectInfo: [rejectStatus] })));
    this.setRejectStatus({ cartItemIds, rejectStatus });
    amplitude.getInstance().logEvent('Смена статуса отказа от вещи в корзине', { cartItemIds, rejectStatus });
  }

  @Action({ rawError: true })
  async saveItemsAvailable({
    cartItemIds, available,
  }: { cartItemIds: number[]; available: boolean }) {
    await services.shoppingApi()
      .shoppingEndpointEditCartItem(cartItemIds.map(id => ({ id, available })));
    this.setAvailable({ cartItemIds, available });
    amplitude.getInstance().logEvent('Смена статуса наличия вещи в корзине', { cartItemIds, available });
  }

  @Action({ rawError: true })
  async deleteShoppingItems(shoppingItemIds: number[]) {
    const shoppingId = this.current && this.current.id;
    if (shoppingId != null) {
      await services.shoppingApi()
        .shoppingEndpointDeleteItem(shoppingItemIds);
      this.removeItem(shoppingItemIds);
      amplitude.getInstance().logEvent('Удаление вещей из корзины', shoppingItemIds);
    }
  }

  @Action({ rawError: true })
  async changeItemStatus({ id, status }: {id: number[]; status: CartItemDTOStatusEnum}) {
    try {
      await services.shoppingApi().shoppingEndpointChangeStatusOfShoppingItems(status, id);
      if (this.current && this.current.id != null) {
        await this.fetchShopping(this.current.id);
      }
      amplitude.getInstance().logEvent('Смена статуса вещи в корзине', { id, status });
    } catch (e) {
      throwError('Ошибка изменения статуса заказа')(e);
    }
  }

  @Action({ rawError: true })
  async fetchComments(cartItemId: number) {
    try {
      const { data: comments } = await services.commentsApi()
        .commentEndpointGetShoppingItemComments(cartItemId);
      this.setComments(comments);
      amplitude.getInstance().logEvent('Загрузка комментариев о веще в корзине', cartItemId);
    } catch (e) {
      throwError('Ошибка получения списка комментариев');
    }
  }

  @Action({ rawError: true })
  async saveComment({ cartItemId, text, author }:
      {cartItemId: number, text: string; author: AuthorDTO}) {
    try {
      const { data: id } = await services.commentsApi()
        .commentEndpointCommentShoppingItem(cartItemId, text);
      this.addComments({ id, text, author });
      amplitude.getInstance().logEvent('Сохранение комментария о веще в корзине', { cartItemId, text, author });
    } catch (e) {
      throwError('Ошибка получения списка комментариев');
    }
  }
}
